import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/utils/apiClient';
import {
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Play,
  Pause,
  AlertTriangle,
  Star,
  User,
  Calendar,
  Music,
  Trash2
} from 'lucide-react';
import { categories } from '@/data/categories';
import MinimalAudioPlayer from '@/components/MinimalAudioPlayer';

interface CommunitySession {
  id: string;
  title: string;
  category_id: string;
  content: string;
  audio_url: string | null;
  background_audio_url?: string | null;
  duration: number;
  session_type: string;
  tags: string[];
  background_music_file?: string | null;
  background_music_volume_db?: number | null;
  is_anonymous: boolean;
  submitted_by_name: string | null;
  content_warning: string | null;
  is_primary: boolean;
  created_at: string;
  approved_at: string | null;
  approval_status: 'pending' | 'approved' | 'rejected' | 'user_revoked' | 'admin_revoked';
  revoked_at: string | null;
  revoked_by_user_id: string | null;
  revocation_reason: string | null;
}

export const CommunitySessionsReview: React.FC = () => {
  const { toast } = useToast();
  const [currentTab, setCurrentTab] = useState<'pending' | 'approved' | 'rejected' | 'user_revoked' | 'admin_revoked'>('pending');
  const [sessions, setSessions] = useState<CommunitySession[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<CommunitySession | null>(null);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [rejectionReason, setRejectionReason] = useState('');
  const [markAsFeatured, setMarkAsFeatured] = useState(false);
  const [addContentWarning, setAddContentWarning] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [playingSessionId, setPlayingSessionId] = useState<string | null>(null);
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false);
  const [revocationReason, setRevocationReason] = useState('');
  const [isRevoking, setIsRevoking] = useState(false);

  useEffect(() => {
    fetchSessions(currentTab);
  }, [currentTab]);

  const fetchSessions = async (status: string) => {
    try {
      setLoading(true);
      if (status === 'pending') {
        const response = await apiClient.get<{ submissions: CommunitySession[]; total_count: number }>(
          '/community-sessions/admin/pending',
          { includeAuth: true }
        );
        setSessions(response.submissions);
      } else {
        const response = await apiClient.get<CommunitySession[]>(
          `/community-sessions/admin/all?status_filter=${status}`,
          { includeAuth: true }
        );
        setSessions(response);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast({
        title: "Error",
        description: `Failed to load ${status} sessions`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReview = (session: CommunitySession, action: 'approve' | 'reject') => {
    setSelectedSession(session);
    setReviewAction(action);
    setRejectionReason('');
    setMarkAsFeatured(false);
    setAddContentWarning(false);
    setIsReviewDialogOpen(true);
  };

  const submitReview = async () => {
    if (!selectedSession) return;

    if (reviewAction === 'reject' && !rejectionReason.trim()) {
      toast({
        title: "Rejection reason required",
        description: "Please provide a reason for rejecting this submission",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsProcessing(true);

      await apiClient.post(`/community-sessions/admin/review/${selectedSession.id}`, {
        action: reviewAction,
        rejection_reason: reviewAction === 'reject' ? rejectionReason : null,
        is_primary: reviewAction === 'approve' ? markAsFeatured : false,
        content_warning: reviewAction === 'approve' && addContentWarning ? 'PG' : null,
      });

      toast({
        title: reviewAction === 'approve' ? "Session Approved" : "Session Rejected",
        description: reviewAction === 'approve'
          ? "The session is now available in the community library"
          : "The user will be notified of the rejection",
      });

      // Remove from current list and refresh
      setSessions((prev) => prev.filter((s) => s.id !== selectedSession.id));
      setIsReviewDialogOpen(false);
      setSelectedSession(null);
    } catch (error: any) {
      console.error('Error processing review:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process review",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRevoke = (session: CommunitySession) => {
    setSelectedSession(session);
    setRevocationReason('');
    setIsRevokeDialogOpen(true);
  };

  const submitRevoke = async () => {
    if (!selectedSession) return;

    try {
      setIsRevoking(true);

      await apiClient.post(`/community-sessions/admin/revoke/${selectedSession.id}`, {
        revocation_reason: revocationReason.trim() || null,
      }, {
        includeAuth: true
      });

      toast({
        title: "Session Revoked",
        description: "The session has been removed from the public library",
      });

      // Remove from approved list
      setSessions((prev) => prev.filter((s) => s.id !== selectedSession.id));
      setIsRevokeDialogOpen(false);
      setSelectedSession(null);
      setRevocationReason('');
    } catch (error: any) {
      console.error('Error revoking session:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to revoke session",
        variant: "destructive",
      });
    } finally {
      setIsRevoking(false);
    }
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || categoryId;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading submissions...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Community Session Submissions
              </CardTitle>
              <CardDescription>
                Review and moderate user-submitted sessions for the community library
              </CardDescription>
            </div>
            <Badge variant="secondary" className="text-lg px-3 py-1">
              {sessions.length} {currentTab.charAt(0).toUpperCase() + currentTab.slice(1).replace('_', ' ')}
            </Badge>
          </div>
        </CardHeader>
      </Card>

      <Tabs value={currentTab} onValueChange={(value: any) => setCurrentTab(value)}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="user_revoked">User Revoked</TabsTrigger>
          <TabsTrigger value="admin_revoked">Admin Revoked</TabsTrigger>
        </TabsList>

        <TabsContent value={currentTab} className="mt-4">
          {sessions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Sessions</h3>
                <p className="text-muted-foreground">No {currentTab.replace('_', ' ')} sessions found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {sessions.map((session) => (
            <Card key={session.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">{session.title}</h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline">{getCategoryName(session.category_id)}</Badge>
                          <Badge variant="secondary">{session.session_type}</Badge>
                          {session.content_warning && (
                            <Badge variant="destructive" className="text-xs">
                              {session.content_warning}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Meta Information */}
                    <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        {session.is_anonymous ? (
                          <span className="italic">Anonymous</span>
                        ) : (
                          <span>{session.submitted_by_name || 'Unknown User'}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(session.created_at)}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {formatDuration(session.duration)}
                      </div>
                      {session.audio_url && (
                        <div className="flex items-center gap-1">
                          <Music className="h-4 w-4" />
                          <span>Audio Available</span>
                        </div>
                      )}
                    </div>

                    {/* Content Preview */}
                    <div className="bg-muted/50 p-3 rounded-md">
                      <p className="text-sm line-clamp-3">{session.content}</p>
                    </div>

                    {/* Audio Player - Shows inline when Listen is clicked */}
                    {playingSessionId === session.id && session.audio_url && (
                      <div className="pt-3 border-t border-border/50">
                        <MinimalAudioPlayer
                          audioUrl={session.audio_url}
                          onEnded={() => setPlayingSessionId(null)}
                        />
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2">
                    {session.approval_status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleReview(session, 'approve')}
                          className="gap-1"
                        >
                          <CheckCircle className="h-4 w-4" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleReview(session, 'reject')}
                          className="gap-1"
                        >
                          <XCircle className="h-4 w-4" />
                          Reject
                        </Button>
                      </>
                    )}
                    {session.approval_status === 'approved' && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRevoke(session)}
                        className="gap-1"
                      >
                        <Trash2 className="h-4 w-4" />
                        Revoke
                      </Button>
                    )}
                    {session.audio_url && (
                      <Button
                        size="sm"
                        variant={playingSessionId === session.id ? "default" : "outline"}
                        onClick={() => setPlayingSessionId(playingSessionId === session.id ? null : session.id)}
                        className="gap-1"
                      >
                        {playingSessionId === session.id ? (
                          <>
                            <Pause className="h-4 w-4" />
                            Hide
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4" />
                            Listen
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={isReviewDialogOpen} onOpenChange={setIsReviewDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {reviewAction === 'approve' ? 'Approve' : 'Reject'} Session
            </DialogTitle>
            <DialogDescription>
              {selectedSession?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {reviewAction === 'approve' ? (
              <>
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    This session will be published to the community library and visible to all users.
                  </AlertDescription>
                </Alert>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="featured"
                      checked={markAsFeatured}
                      onCheckedChange={(checked) => setMarkAsFeatured(!!checked)}
                    />
                    <Label htmlFor="featured" className="cursor-pointer flex items-center gap-2">
                      <Star className="h-4 w-4" />
                      Feature on homepage
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="content-warning"
                      checked={addContentWarning}
                      onCheckedChange={(checked) => setAddContentWarning(!!checked)}
                    />
                    <Label htmlFor="content-warning" className="cursor-pointer flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Add PG content warning
                    </Label>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>
                    This session will be rejected and the user will be notified.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="rejection-reason">Reason for Rejection *</Label>
                  <Textarea
                    id="rejection-reason"
                    placeholder="E.g., Content violates community guidelines (racism/antagonization/extremism), Poor audio quality, Inappropriate content, etc."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">
                    This will be shown to the user. Please be specific and constructive.
                  </p>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsReviewDialogOpen(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={submitReview}
              disabled={isProcessing || (reviewAction === 'reject' && !rejectionReason.trim())}
              variant={reviewAction === 'approve' ? 'default' : 'destructive'}
            >
              {isProcessing
                ? 'Processing...'
                : reviewAction === 'approve'
                ? 'Approve & Publish'
                : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Dialog */}
      <Dialog open={isRevokeDialogOpen} onOpenChange={setIsRevokeDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Revoke Community Session</DialogTitle>
            <DialogDescription>
              {selectedSession?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This will immediately remove the session from the public community library.
                The action cannot be undone.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="revocation-reason">Reason for Revocation (Optional)</Label>
              <Textarea
                id="revocation-reason"
                placeholder="E.g., Violates community guidelines, Inappropriate content, Copyright issues, etc."
                value={revocationReason}
                onChange={(e) => setRevocationReason(e.target.value)}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                This reason will be shown to the user who submitted the session.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsRevokeDialogOpen(false);
                setRevocationReason('');
              }}
              disabled={isRevoking}
            >
              Cancel
            </Button>
            <Button
              onClick={submitRevoke}
              disabled={isRevoking}
              variant="destructive"
            >
              {isRevoking ? 'Revoking...' : 'Revoke Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
